<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  // Hidden when picking a rubro for an assistant that already exists: "start from scratch"
  // makes sense when creating one, not when linking an existing assistant to a rubro.
  showScratch: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['select', 'scratch']);

const { t } = useI18n();
const store = useStore();

const presets = ref([]);
const isLoading = ref(true);

const ICON_MAP = {
  package: 'i-lucide-package',
  'graduation-cap': 'i-lucide-graduation-cap',
  briefcase: 'i-lucide-briefcase',
  store: 'i-lucide-store',
  stethoscope: 'i-lucide-stethoscope',
};

onMounted(async () => {
  try {
    const data = await store.dispatch('captainAssistants/fetchBusinessPresets');
    presets.value = data || [];
  } catch {
    presets.value = [];
  } finally {
    isLoading.value = false;
  }
});

const handleSelect = preset => emit('select', preset);
const handleScratch = () => emit('scratch');
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ t('CAPTAIN.ASSISTANTS.PRESET.SELECT_DESCRIPTION') }}
    </p>

    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <span class="i-lucide-loader-2 animate-spin mr-2" />
      {{ t('CAPTAIN.ASSISTANTS.PRESET.LOADING') }}
    </div>

    <div v-else-if="presets.length" class="grid grid-cols-2 gap-3">
      <button
        v-for="preset in presets"
        :key="preset.preset_id"
        class="flex flex-col items-start gap-2 p-4 rounded-lg border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-3 hover:border-n-brand cursor-pointer transition-all text-left"
        @click="handleSelect(preset)"
      >
        <span
          class="text-lg"
          :class="ICON_MAP[preset.icon] || 'i-lucide-briefcase'"
        />
        <span class="text-sm font-semibold text-n-slate-12">
          {{ preset.title }}
        </span>
        <span class="text-xs text-n-slate-11 line-clamp-3">
          {{ preset.description }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{
            t('CAPTAIN.ASSISTANTS.PRESET.SCENARIO_COUNT', {
              count: preset.scenarios ? preset.scenarios.length : 0,
            })
          }}
        </span>
      </button>
    </div>

    <div v-if="props.showScratch" class="border-t border-n-weak pt-4">
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-lucide-plus"
        :label="t('CAPTAIN.ASSISTANTS.PRESET.START_FROM_SCRATCH')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleScratch"
      />
    </div>
  </div>
</template>
